import React, { useState, useEffect } from 'react'
import {
  useDataProvider,
  useNotify,
  Button,
  useRefresh,
  useRecordContext,
} from 'react-admin'
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Divider,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material'
import OrganisationVendorForm from './OrganisationVendorForm'
import VendorFormLayout from '../components/VendorFormLayout'

// Define the vendor form type
interface VendorForm {
  id: string
  name: string
  description: string
  vendor_form: any[]
}

const OrganisationVendorFormDirectory = () => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const record = useRecordContext()

  // State to store the vendor forms
  const [vendorForms, setVendorForms] = useState<VendorForm[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedForm, setSelectedForm] = useState<VendorForm | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [newFormName, setNewFormName] = useState('')
  const [newFormDescription, setNewFormDescription] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [formToDelete, setFormToDelete] = useState<string | null>(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [formToView, setFormToView] = useState<VendorForm | null>(null)

  // Reset the create form
  const resetCreateForm = () => {
    setNewFormName('')
    setNewFormDescription('')
  }

  // Load the organisation's vendor forms
  useEffect(() => {
    const loadVendorForms = async () => {
      try {
        setLoading(true)
        const organisationId = record?.id
        const endpoint = organisationId
          ? `vendor-forms/organisations/${organisationId}`
          : 'organisations/current/vendor-forms'
        const response = await dataProvider.custom(endpoint, {
          method: 'GET',
        })

        setVendorForms(response.data || [])
      } catch (error) {
        console.error('Error loading vendor forms:', error)
        notify('Error loading vendor forms', { type: 'error' })
        setVendorForms([])
      } finally {
        setLoading(false)
      }
    }

    loadVendorForms()
  }, [dataProvider, notify, record])

  // Handle creating a new vendor form
  const handleCreateForm = async () => {
    if (!newFormName.trim()) {
      notify('Form name is required', { type: 'warning' })
      return
    }

    try {
      setIsCreating(true)
      const organisationId = record?.id
      const endpoint = organisationId
        ? `vendor-forms/organisations/${organisationId}`
        : 'organisations/current/vendor-forms'

      await dataProvider.custom(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          name: newFormName,
          description: newFormDescription,
        }),
      })

      notify('Vendor form created successfully', { type: 'success' })
      setIsCreateDialogOpen(false)
      resetCreateForm()
      refresh()

      // Reload the vendor forms
      const response = await dataProvider.custom(endpoint, {
        method: 'GET',
      })
      setVendorForms(response.data || [])
    } catch (error) {
      console.error('Error creating vendor form:', error)
      notify('Error creating vendor form', { type: 'error' })
    } finally {
      setIsCreating(false)
    }
  }

  // Show delete confirmation dialog
  const showDeleteConfirmation = (formId: string) => {
    setFormToDelete(formId)
    setIsDeleteDialogOpen(true)
  }

  // Handle deleting a vendor form
  const handleDeleteForm = async () => {
    if (!formToDelete) return

    try {
      setIsDeleting(true)
      const organisationId = record?.id
      const endpoint = organisationId
        ? `vendor-forms/${formToDelete}/organisations/${organisationId}`
        : `organisations/current/vendor-forms/${formToDelete}`

      await dataProvider.custom(endpoint, {
        method: 'DELETE',
      })

      notify('Vendor form deleted successfully', { type: 'success' })

      // Update the local state to remove the deleted form
      setVendorForms(vendorForms.filter((form) => form.id !== formToDelete))

      // Close the dialog
      setIsDeleteDialogOpen(false)
      setFormToDelete(null)
    } catch (error) {
      console.error('Error deleting vendor form:', error)
      notify('Error deleting vendor form', { type: 'error' })
    } finally {
      setIsDeleting(false)
    }
  }

  // Handle opening the edit modal
  const handleEditForm = (form: VendorForm) => {
    setSelectedForm(form)
    setIsModalOpen(true)
  }

  // Handle opening the view modal
  const handleViewForm = (form: VendorForm) => {
    setFormToView(form)
    setIsViewModalOpen(true)
  }

  // Handle closing the edit modal
  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedForm(null)
    // Refresh the list after editing
    refresh()
  }

  // Handle closing the view modal
  const handleCloseViewModal = () => {
    setIsViewModalOpen(false)
    setFormToView(null)
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'visible' }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Vendor Forms</Typography>
        <Button
          label="Create New Form"
          onClick={() => setIsCreateDialogOpen(true)}
          startIcon={<AddIcon />}
        />
      </Box>

      {vendorForms.length === 0 ? (
        <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            No vendor forms found. Create your first vendor form to get started.
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={1}>
          <List>
            {vendorForms.map((form, index) => (
              <React.Fragment key={form.id}>
                {index > 0 && <Divider />}
                <ListItem>
                  <ListItemText
                    primary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          fontWeight="bold"
                          sx={{ mr: 1 }}
                        >
                          Name:
                        </Typography>
                        {form.name}
                      </>
                    }
                    secondary={
                      <>
                        {form.description && (
                          <Typography
                            component="span"
                            variant="body2"
                            color="textSecondary"
                            sx={{ display: 'block' }}
                          >
                            <Typography
                              component="span"
                              variant="body2"
                              fontWeight="bold"
                              sx={{ mr: 1 }}
                            >
                              Description:
                            </Typography>
                            {form.description}
                          </Typography>
                        )}
                        <Typography
                          component="span"
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            display: 'block',
                          }}
                        >
                          Form ID: {form.id}
                        </Typography>
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="view"
                      onClick={() => handleViewForm(form)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="edit"
                      onClick={() => handleEditForm(form)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => showDeleteConfirmation(form.id)}
                      disabled={isDeleting}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {/* Create Form Dialog */}
      <Dialog
        open={isCreateDialogOpen}
        onClose={() => {
          setIsCreateDialogOpen(false)
          resetCreateForm()
        }}
        aria-labelledby="create-form-dialog-title"
      >
        <DialogTitle id="create-form-dialog-title">
          Create New Vendor Form
          <IconButton
            aria-label="close"
            onClick={() => {
              setIsCreateDialogOpen(false)
              resetCreateForm()
            }}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Form Name"
            type="text"
            fullWidth
            value={newFormName}
            onChange={(e) => setNewFormName(e.target.value)}
          />
          <TextField
            margin="dense"
            id="description"
            label="Form Description"
            type="text"
            fullWidth
            multiline
            rows={3}
            value={newFormDescription}
            onChange={(e) => setNewFormDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            label="Cancel"
            onClick={() => {
              setIsCreateDialogOpen(false)
              resetCreateForm()
            }}
            disabled={isCreating}
          />
          <Button
            label={isCreating ? 'Creating...' : 'Create'}
            onClick={handleCreateForm}
            disabled={isCreating || !newFormName.trim()}
          />
        </DialogActions>
      </Dialog>

      {/* Edit Form Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
        aria-labelledby="edit-form-dialog-title"
      >
        <DialogTitle id="edit-form-dialog-title">
          Edit Vendor Form: {selectedForm?.name}
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedForm && (
            <OrganisationVendorForm
              formId={selectedForm.id}
              isModal
              onClose={handleCloseModal}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* View Form Modal */}
      <Dialog
        open={isViewModalOpen}
        onClose={handleCloseViewModal}
        fullWidth
        maxWidth="md"
        aria-labelledby="view-form-dialog-title"
      >
        <DialogTitle id="view-form-dialog-title">
          View Vendor Form: {formToView?.name}
          <IconButton
            aria-label="close"
            onClick={handleCloseViewModal}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {formToView && (
            <VendorFormLayout
              formSections={formToView.vendor_form || []}
              formName={formToView.name}
              formDescription={formToView.description}
              readOnly
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            label="Close"
            onClick={handleCloseViewModal}
            variant="outlined"
          />
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false)
          setFormToDelete(null)
        }}
        aria-labelledby="delete-form-dialog-title"
      >
        <DialogTitle id="delete-form-dialog-title">
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this vendor form? This action cannot
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            label="Cancel"
            onClick={() => {
              setIsDeleteDialogOpen(false)
              setFormToDelete(null)
            }}
            disabled={isDeleting}
          />
          <Button
            label={isDeleting ? 'Deleting...' : 'Delete'}
            onClick={handleDeleteForm}
            disabled={isDeleting}
            color="primary"
          />
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default OrganisationVendorFormDirectory
